import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Languages, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_LABELS = { en: 'English', es: 'Español' };
const SOURCE_LABELS = { onboarding: 'First-run picker', settings: 'Settings switch' };
const BAR = { en: 'bg-sky-500', es: 'bg-amber-500' };

/**
 * LanguageSplitCard — SuperAdmin view of the English/Spanish split
 * (`GET /api/analytics/language/summary`): overall language mix, the
 * first-run onboarding Spanish share, and picks broken down by source.
 */
const LanguageSplitCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/analytics/language/summary`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load language analytics');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byLang = data?.by_language || {};
  const bySource = data?.by_source || {};
  const total = data?.total || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <Card className="border-2 border-indigo-300/50 dark:border-indigo-700/40" data-testid="language-split-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
            <Languages className="w-5 h-5" /> Language Split — English / Spanish
          </span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="language-split-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {loading && !data && (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {error && <p className="text-red-600 italic" data-testid="language-split-error">{error}</p>}
        {data && total === 0 && (
          <p className="text-slate-500 italic" data-testid="language-split-empty">
            No language picks recorded yet. As new readers choose English or Spanish in the first-run picker (or switch in settings), the split appears here.
          </p>
        )}
        {data && total > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3" data-testid="language-split-metrics">
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Total picks</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{Math.round((data.spanish_share || 0) * 100)}%</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Spanish</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{Math.round((data.onboarding_spanish_share || 0) * 100)}%</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">ES @ signup</p>
              </div>
            </div>

            <div className="space-y-2" data-testid="language-split-bars">
              {['en', 'es'].map((lang) => (
                <div key={lang} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{LANG_LABELS[lang]}</span>
                    <span className="text-xs text-slate-500">{byLang[lang] || 0} · {pct(byLang[lang] || 0)}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full ${BAR[lang]} transition-all`} style={{ width: `${pct(byLang[lang] || 0)}%` }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2" data-testid="language-split-sources">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Where readers picked their language</p>
              {Object.entries(bySource).map(([src, langs]) => (
                <div key={src} className="flex items-center justify-between rounded-lg bg-muted/60 px-3 py-2">
                  <span className="font-medium">{SOURCE_LABELS[src] || src}</span>
                  <span className="flex gap-1.5">
                    {['en', 'es'].map((l) => (
                      <Badge key={l} variant="secondary">{LANG_LABELS[l]}: {langs[l] || 0}</Badge>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LanguageSplitCard;
